import { useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { searchableUserMenuStyle } from '../searchableUserMenu'
import { Icon } from './Icon'

interface Props {
  value: string
  options: string[]
  onChange: (value: string) => void
  disabled?: boolean
  ariaLabel: string
  placeholder: string
}

function normalized(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR')
}

export function filterMaterialOptions(options: string[], query: string) {
  const normalizedQuery = normalized(query.trim())
  return normalizedQuery ? options.filter(option => normalized(option).includes(normalizedQuery)) : options
}

export function SearchableMaterialSelect({ value, options, onChange, disabled = false, ariaLabel, placeholder }: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({})
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listboxId = useId()
  const selected = options.find(option => option === value)
  const filtered = filterMaterialOptions(options, query)

  useEffect(() => {
    if (!open) return

    function positionMenu() {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      const bodyZoom = Number.parseFloat(window.getComputedStyle(document.body).zoom) || 1
      setMenuStyle(searchableUserMenuStyle(rect, { width: window.innerWidth, height: window.innerHeight }, bodyZoom))
    }

    function closeOutside(event: PointerEvent) {
      if (!(event.target instanceof Node)) return
      if (!triggerRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) setOpen(false)
    }

    function closeWithEscape(event: globalThis.KeyboardEvent) {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      setOpen(false)
      triggerRef.current?.focus()
    }

    positionMenu()
    requestAnimationFrame(() => inputRef.current?.focus())
    document.addEventListener('pointerdown', closeOutside)
    document.addEventListener('keydown', closeWithEscape)
    window.addEventListener('resize', positionMenu)
    window.addEventListener('scroll', positionMenu, true)
    return () => {
      document.removeEventListener('pointerdown', closeOutside)
      document.removeEventListener('keydown', closeWithEscape)
      window.removeEventListener('resize', positionMenu)
      window.removeEventListener('scroll', positionMenu, true)
    }
  }, [open])

  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])

  function toggle() {
    if (disabled) return
    setQuery('')
    setOpen(current => !current)
  }

  function handleTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      if (!open) toggle()
    }
  }

  function select(option: string) {
    onChange(option)
    setOpen(false)
    setQuery('')
    triggerRef.current?.focus()
  }

  return <div className={`searchable-user-select searchable-material-select ${disabled ? 'disabled' : ''}`}>
    <button ref={triggerRef} type="button" className="searchable-user-trigger searchable-material-trigger" disabled={disabled} aria-label={ariaLabel} aria-haspopup="listbox" aria-controls={listboxId} aria-expanded={open} onClick={toggle} onKeyDown={handleTriggerKeyDown}>
      <span className="searchable-user-selection"><b>{selected ?? placeholder}</b></span><span className="searchable-user-chevron" aria-hidden="true">⌄</span>
    </button>
    {open && createPortal(<div ref={menuRef} className="searchable-user-menu searchable-material-menu" style={menuStyle}>
      <label className="searchable-user-search"><Icon name="search"/><span className="sr-only">Pesquisar material</span><input ref={inputRef} value={query} onChange={event => setQuery(event.target.value)} placeholder="Pesquisar material" aria-label="Pesquisar material"/></label>
      <div id={listboxId} className="searchable-user-options searchable-material-options" role="listbox" aria-label={ariaLabel}>
        {filtered.map(option => <button type="button" role="option" aria-selected={option === value} className={option === value ? 'selected' : ''} key={option} onClick={() => select(option)}><span><b>{option}</b></span>{option === value && <i aria-hidden="true">✓</i>}</button>)}
        {!filtered.length && <div className="searchable-user-empty"><b>Nenhum material encontrado</b><span>Tente pesquisar outro nome.</span></div>}
      </div>
    </div>, document.body)}
  </div>
}
